import type { HTMLInputAttributes } from "svelte/elements";
import { Locale, type LocalizedString } from "@core/schema/Locale.js";
import { sameValueForEveryLocale } from "@core/utils/sameValueForEveryLocale.js";

const customValidationMessage: Record<NonNullable<HTMLInputAttributes["type"]>, LocalizedString> = {
	email: {
		[Locale.cs]: "Zadejte platnou e-mailovou adresu",
		[Locale.en]: "Enter a valid email address",
	},
	url: {
		[Locale.cs]: "URL adresa (včetně https://)",
		[Locale.en]: "URL address (including https://)",
	},
};

export const customValidationMessages: Record<
	keyof ValidityState,
	(input: HTMLInputElement | HTMLSelectElement) => LocalizedString
> = {
	valid: () => sameValueForEveryLocale(""),
	customError: ({ validationMessage }) => sameValueForEveryLocale(validationMessage),
	valueMissing: () => ({
		[Locale.cs]: "Toto pole je povinné",
		[Locale.en]: "This field is required",
	}),
	typeMismatch: ({ type }) =>
		customValidationMessage[type] ?? {
			[Locale.cs]: "Zadejte platnou hodnotu",
			[Locale.en]: "Enter a valid value",
		},
	patternMismatch: () => ({
		[Locale.cs]: "Zadejte hodnotu ve správném formátu",
		[Locale.en]: "Enter a value in the correct format",
	}),
	badInput: () => ({
		[Locale.cs]: "Zadejte platnou hodnotu",
		[Locale.en]: "Enter a valid value",
	}),
	rangeOverflow: (input) => ({
		[Locale.cs]: `Zadejte hodnotu menší nebo rovnou ${input instanceof HTMLInputElement ? input.max : ""}`,
		[Locale.en]: `Enter a value less than or equal to ${input instanceof HTMLInputElement ? input.max : ""}`,
	}),
	rangeUnderflow: (input) => ({
		[Locale.cs]: `Zadejte hodnotu větší nebo rovnou ${input instanceof HTMLInputElement ? input.min : ""}`,
		[Locale.en]: `Enter a value greater than or equal to ${input instanceof HTMLInputElement ? input.min : ""}`,
	}),
	stepMismatch: (input) => ({
		[Locale.cs]: `Zadejte hodnotu dělitelnou ${input instanceof HTMLInputElement ? input.step : ""}`,
		[Locale.en]: `Enter a value that matches the specified step ${input instanceof HTMLInputElement ? input.step : ""}`,
	}),
	tooLong: (input) => ({
		[Locale.cs]: `Zadejte nejvýše ${input instanceof HTMLInputElement ? input.maxLength : ""} znaků`,
		[Locale.en]: `Enter no more than ${input instanceof HTMLInputElement ? input.maxLength : ""} characters`,
	}),
	tooShort: (input) => ({
		[Locale.cs]: `Zadejte nejméně ${input instanceof HTMLInputElement ? input.minLength : ""} znaků`,
		[Locale.en]: `Enter at least ${input instanceof HTMLInputElement ? input.minLength : ""} characters`,
	}),
};
